<template>
    <v-dialog :value="show" width="720" scrollable
        @input="$emit('update:show', $event)">
        <div class="hv" v-if="media">
            <div class="hv-head">
                <div>
                    <div class="hv-title">{{ $t('media.hotspot.preview_title') }}</div>
                    <div class="hv-sub">{{ media.title }}</div>
                </div>
                <v-btn icon small @click="$emit('update:show', false)">
                    <v-icon size="20">mdi-close</v-icon>
                </v-btn>
            </div>

            <div class="hv-body">
                <!-- ⚠⚠ **這張 img 必須自己就是定位框**（`max-width/max-height` 在圖上、
                     外層 inline-block 收縮到圖）。外面包固定比例 + `object-fit: contain`
                     會讓圖留白置中，於是「容器的百分比」不等於「圖的百分比」，
                     每個框都偏移——800×600 放進 1.47:1 的框會偏 4.7%。
                     學生端 `FindDifference.vue` 用的就是 `w-full h-auto`。
                     ⚠ 同一個坑在 `HotspotPicker` 用裁切的方式踩過一次（2026-09-04）。 -->
                <div class="hv-box">
                    <div class="hv-stage">
                        <img :src="media.url" class="hv-img" :alt="media.title">
                        <span v-for="t in targets" :key="t.id"
                            class="hv-target" :style="targetStyle(t)" />
                    </div>
                </div>

                <div class="hv-foot">
                    {{ $t('unit.settings.hotspot_count', { n: targets.length }) }}
                    <!-- ⚠ 只對「不是自己的」講「不能調整」。自己上傳的可以編輯。 -->
                    <template v-if="!media.is_mine">
                        —— {{ $t('unit.settings.hotspot_readonly') }}
                    </template>
                </div>
                <div v-if="!hasSize" class="hv-bad">
                    {{ $t('media.hotspot.err_no_size') }}
                </div>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { attrsOf } from './mediaConstants';

/**
 * 找不同圖的**唯讀**預覽：把 `attributes.targets` 畫在圖上。
 *
 * ⚠ **為什麼熱區圖不用「在新分頁開啟」**（2026-09-04）：新分頁開的是原始圖檔，
 * 那上面**看不到任何熱區**——而熱區正是老師要確認的東西。對圖片／PDF／影片，
 * 原始檔就是內容，新分頁是對的；熱區圖的內容是「圖 + 答案」，答案不在檔案裡。
 *
 * ⚠ 這是**唯讀**的，不要在這裡加編輯功能。編輯走 `HotspotEditDialog`
 * （它才有只能一個框、最小邊長之類的規則）。
 */
export default {
    name: 'HotspotPreviewDialog',
    props: {
        show: { type: Boolean, default: false },
        /** 要預覽的熱區素材。 */
        media: { type: Object, default: null },
    },
    computed: {
        targets() {
            const t = this.media ? attrsOf(this.media).targets : null;
            return Array.isArray(t) ? t : [];
        },
        /**
         * ⚠ `width`/`height` 是熱區座標的座標系（ADR 0004）。沒有它們就換算不出百分比
         * ——那時要**明講**而不是畫出一堆位置錯誤的框（後者老師看不出是壞的）。
         */
        hasSize() {
            return !!(this.media && this.media.width && this.media.height);
        },
    },
    methods: {
        /**
         * 熱區框的位置。
         * ⚠ 公式與學生端的 `FindDifference.vue` 一致（`pxX / originalSize.w * 100`）
         * ——那邊算錯一個位元，老師在這裡看到的框就跟學生點得到的位置對不上。
         */
        targetStyle(t) {
            if (!this.hasSize) {
                return { display: 'none' };
            }
            const w = this.media.width;
            const h = this.media.height;
            return {
                left: `${(t.pxX / w) * 100}%`,
                top: `${(t.pxY / h) * 100}%`,
                width: `${(t.pxW / w) * 100}%`,
                height: `${(t.pxH / h) * 100}%`,
            };
        },
    },
};
</script>

<style scoped>
.hv {
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
}

.hv-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 20px 12px;
    border-bottom: 1px solid #eceff1;
}

.hv-title {
    font-size: 17px;
    font-weight: 600;
    color: #263238;
}

.hv-sub {
    font-size: 12px;
    color: #78909c;
    margin-top: 2px;
    word-break: break-all;
}

.hv-body {
    padding: 16px 20px;
    overflow-y: auto;
}

.hv-box {
    display: flex;
    justify-content: center;
    background: #eceff1;
}

/* ⚠ inline-block 收縮到圖片的實際渲染尺寸，所以百分比定位的框對得上。
   ⚠ **不要在這一層加 max-height 或 overflow**——那會裁掉圖而框仍按百分比定位，
   於是兩者對不上（`HotspotPicker` 踩過這個坑）。尺寸限制放在 `.hv-img`。 */
.hv-stage {
    position: relative;
    display: inline-block;
    max-width: 100%;
    line-height: 0;
}

.hv-img {
    display: block;
    max-width: 100%;
    max-height: 60vh;
    width: auto;
    height: auto;
}

/* 外框 + 半透明底兩者都要——純外框在深色圖上看不見。
   顏色與挑選器（`.mp-target`）、框選工具（`.hp-rect`）一致。 */
.hv-target {
    position: absolute;
    border: 2px solid #fb923c;
    background: rgba(251, 146, 60, 0.22);
    border-radius: 2px;
}

.hv-foot {
    font-size: 12px;
    color: #607d8b;
    margin-top: 10px;
    line-height: 1.6;
}

.hv-bad {
    font-size: 12px;
    color: #e53935;
    margin-top: 6px;
    line-height: 1.5;
}
</style>
